import { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import GoogleWebButton, { googleNonce } from '../components/GoogleWebButton';
import { useAuth } from '../context/AuthContext';
import api from '../api/client';
import PageMeta from '../components/PageMeta';
import PasswordInput from '../components/PasswordInput';
import {
  isNativePlatform, isNativeGoogleAuthAvailable, nativeGoogleSignIn,
  isNativeAppleAuthAvailable, nativeAppleSignIn,
} from '../lib/nativeSocialAuth';
import { GoogleSignInButton, AppleSignInButton } from '../components/SocialAuthButtons';
import AppleEmailPrompt from '../components/AppleEmailPrompt';
import { authErrorMessage } from '../lib/authErrorMessage';
import useContainerWidth from '../hooks/useContainerWidth';
import { googleButtonWidth } from '../lib/googleButtonWidth';

export default function Register() {
  // The Google button can't stretch with CSS; it's sized to this container (lib/googleButtonWidth.js).
  const [googleRef, googleContainerWidth] = useContainerWidth();
  const { login } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const nonce = googleNonce();

  function validatePassword(pw) {
    if (pw.length < 8) return 'Password must be at least 8 characters';
    if (!/[A-Z]/.test(pw)) return 'Password must contain at least one uppercase letter';
    if (!/[0-9]/.test(pw)) return 'Password must contain at least one digit';
    if (!/[^A-Za-z0-9]/.test(pw)) return 'Password must contain at least one special character';
    return null;
  }

  async function handleSubmit(e) {
    e.preventDefault();
    setError('');
    const pwError = validatePassword(password);
    if (pwError) { setError(pwError); return; }
    if (password !== confirmPassword) { setError('Passwords do not match'); return; }
    setLoading(true);
    try {
      const { data } = await api.post('/auth/register', { email, password });
      login(data.token);
      navigate('/');
    } catch (err) {
      setError(err.response?.data?.error || 'Registration failed');
    } finally {
      setLoading(false);
    }
  }

  // `nonce` only means anything for the web Google button below, which is the one
  // thing that actually requests a token using it. Native sign-in (both providers) gets its
  // token from the OS SDK directly, with no way to make it embed this React-side nonce — so
  // sending it along anyway just guarantees a nonce-mismatch rejection on the backend, since
  // it can never equal whatever (if anything) ends up in the native token's own nonce claim.
  async function handleGoogleSuccess({ credential }, { includeNonce = true } = {}) {
    setError('');
    try {
      const body = includeNonce ? { credential, nonce } : { credential };
      const { data } = await api.post('/auth/google', body);
      login(data.token);
      navigate('/');
    } catch (err) {
      setError(authErrorMessage(err, 'Google sign-in failed'));
    }
  }

  async function handleNativeGoogleClick() {
    setError('');
    try {
      const result = await nativeGoogleSignIn();
      await handleGoogleSuccess(result, { includeNonce: false });
    } catch (err) {
      // See Login.js's handleNativeGoogleClick for why the real message is surfaced here
      // instead of a generic string — the native plugin preserves real diagnostic detail
      // (including Android OAuth-client misconfiguration) even in what looks like a
      // cancellation, and a bare catch used to discard it. See Login.js for why
      // authErrorMessage() also has to separate "the request never completed" from
      // "sign-in failed" — this catch never sees the POST's own failures.
      setError(authErrorMessage(err, 'Google sign-in failed'));
    }
  }

  // See Login.js's matching block and components/AppleEmailPrompt.js — Apple releases an
  // email only on the first authorization for an Apple ID + app, so /auth/apple can answer
  // `{ requiresEmail: true }` and needs the same identity token re-sent with a typed address.
  const [applePending, setApplePending] = useState(null);
  const [appleEmail, setAppleEmail] = useState('');
  const [appleBusy, setAppleBusy] = useState(false);

  async function handleAppleEmailSubmit(e) {
    e.preventDefault();
    setError('');
    setAppleBusy(true);
    try {
      const { data } = await api.post('/auth/apple', { ...applePending, email: appleEmail.trim() });
      if (data.requiresEmail) {
        setError('Please enter a valid email address.');
        return;
      }
      setApplePending(null);
      setAppleEmail('');
      login(data.token);
      navigate('/');
    } catch (err) {
      const status = err.response?.status;
      setError(status === 401
        ? 'That took too long — tap Continue with Apple again to retry.'
        : err.response?.data?.error || 'Could not finish signing in');
      if (status === 401) { setApplePending(null); setAppleEmail(''); }
    } finally {
      setAppleBusy(false);
    }
  }

  async function handleNativeAppleClick() {
    setError('');
    try {
      const { identityToken, fullName } = await nativeAppleSignIn();
      const { data } = await api.post('/auth/apple', { identityToken, fullName });
      if (data.requiresEmail) {
        setApplePending({ identityToken, fullName });
        return;
      }
      login(data.token);
      navigate('/');
    } catch (err) {
      setError(authErrorMessage(err, 'Apple sign-in failed'));
    }
  }

  if (applePending) {
    return (
      <AppleEmailPrompt
        email={appleEmail}
        onEmailChange={e => setAppleEmail(e.target.value)}
        onSubmit={handleAppleEmailSubmit}
        onCancel={() => { setApplePending(null); setAppleEmail(''); setError(''); }}
        error={error}
        busy={appleBusy}
      />
    );
  }

  return (
    <div className="auth-page">
      <PageMeta title="Create Account" description="Create your free Agence account — connect your bank and brokerage to get AI budget insights and investment analysis." path="/register" />
      <div className="auth-card">
        <h1>Agence</h1>
        <h2>Create account</h2>
        <form onSubmit={handleSubmit}>
          <input
            type="email"
            placeholder="Email"
            autoComplete="email"
            aria-label="Email address"
            value={email}
            onChange={e => setEmail(e.target.value)}
            required
          />
          <PasswordInput
            placeholder="Password (8+ characters)"
            autoComplete="new-password"
            aria-label="Password (8 or more characters, including uppercase, number, and symbol)"
            value={password}
            onChange={e => setPassword(e.target.value)}
            minLength={8}
            required
          />
          <p style={{ margin: '-0.4rem 0 0', fontSize: '0.78rem', color: 'var(--text-muted)', textAlign: 'left' }}>
            Must include an uppercase letter, a number, and a special character.
          </p>
          <PasswordInput
            placeholder="Confirm password"
            autoComplete="new-password"
            aria-label="Confirm password"
            value={confirmPassword}
            onChange={e => setConfirmPassword(e.target.value)}
            required
          />
          {error && <p className="error">{error}</p>}
          <button type="submit" disabled={loading}>
            {loading ? 'Creating account…' : 'Create account'}
          </button>
        </form>
        <div className="auth-divider"><span>or</span></div>
        <div className="auth-google" ref={googleRef}>
          {isNativePlatform() ? (
            isNativeGoogleAuthAvailable() && <GoogleSignInButton onClick={handleNativeGoogleClick} />
          ) : (
            <GoogleWebButton
              onSuccess={handleGoogleSuccess}
              onError={() => setError('Google sign-in failed')}
              width={googleButtonWidth(googleContainerWidth)}
            />
          )}
          {isNativeAppleAuthAvailable() && <AppleSignInButton onClick={handleNativeAppleClick} />}
        </div>
        <p>Already have an account? <Link to="/login">Sign in</Link></p>
      </div>
    </div>
  );
}
